import Vue from "vue";
import axios from "axios";

export const UNIFIED_RISK_TYPE = {
    DUPLICATE_REVIEW: "duplicate_review",
    SIMILAR_IMAGE_REVIEW: "similar_image_review",
    CLAIM_RISK: "claim_risk",
    VIN_PLATE_REPEAT: "vin_plate_repeat",
    RIDER_THIRD_PARTY: "rider_third_party",
};

function resolveHttpClient(httpClient) {
    const resolved = httpClient || Vue.prototype?.instance || axios.create();
    if (!resolved || typeof resolved.get !== "function") {
        return axios.create();
    }
    return resolved;
}

function resolveBaseUrl(apiBaseurl, httpClient) {
    const explicitBase = String(apiBaseurl || "").trim();
    if (explicitBase) {
        return explicitBase.replace(/\/$/, "");
    }
    const runtimeBase = String(httpClient?.defaults?.baseURL || "").trim();
    return runtimeBase.replace(/\/$/, "");
}

function buildUnifiedReviewPath(apiBaseurl, path) {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const base = String(apiBaseurl || "").trim().replace(/\/$/, "");
    if (!base) {
        return `/unified-review${normalizedPath}`;
    }
    return `${base}/unified-review${normalizedPath}`;
}

function buildManagerUnifiedReviewPath(apiBaseurl, path) {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const base = String(apiBaseurl || "").trim().replace(/\/$/, "");
    if (!base) {
        return `/manager/unified-review${normalizedPath}`;
    }
    return `${base}/manager/unified-review${normalizedPath}`;
}

function shouldFallbackToManager(error) {
    const status = Number(error?.response?.status || 0);
    return status === 404 || status === 405;
}

function withManagerFallback(primaryCall, fallbackCall) {
    return primaryCall().catch((error) => {
        if (!shouldFallbackToManager(error)) {
            throw error;
        }
        return fallbackCall();
    });
}

function ensureArray(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (!value) {
        return [];
    }
    return String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
}

function parseJsonPayload(value) {
    if (!value) {
        return {};
    }
    if (typeof value === "object") {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch (error) {
        return {};
    }
}

function isUnifiedRoot(value) {
    return Boolean(
        value &&
        typeof value === "object" &&
        Object.prototype.hasOwnProperty.call(value, "code") &&
        Object.prototype.hasOwnProperty.call(value, "message")
    );
}

function extractRoot(response) {
    if (isUnifiedRoot(response)) {
        return response;
    }
    const payload = response && response.data;
    if (isUnifiedRoot(payload)) {
        return payload;
    }
    return payload || {};
}

function normalizeRecord(record = {}) {
    const primaryCaseNo = record.primaryCaseNo || record.primary_case_no || "";
    const relatedCaseNoList = ensureArray(record.relatedCaseNoList || record.related_case_no_list);
    const detailPayload = parseJsonPayload(record.detailPayload || record.detail_payload);
    const rulePayload = parseJsonPayload(record.rulePayload || record.rule_payload);
    const excludePayload = parseJsonPayload(record.excludePayload || record.exclude_payload);
    const rawRiskEventId = record.riskEventId || record.risk_event_id || record.reviewRecordId;
    const riskEventId = rawRiskEventId == null ? "" : String(rawRiskEventId);
    const rawReviewRecordId = record.reviewRecordId || record.riskEventId || record.risk_event_id;
    const reviewRecordId = rawReviewRecordId == null ? "" : String(rawReviewRecordId);
    return {
        ...record,
        reviewRecordId,
        riskEventId,
        primaryCaseNo,
        relatedCaseNoList,
        detailPayload,
        rulePayload,
        excludePayload,
    };
}

function normalizeListResponse(response, itemMapper) {
    const root = extractRoot(response);
    const payload = root.data || {};
    const items = Array.isArray(payload.items) ? payload.items.map((item) => itemMapper(normalizeRecord(item))) : [];
    return {
        ...response,
        data: {
            ...root,
            data: {
                ...payload,
                items,
            },
        },
    };
}

function normalizeHistoryResponse(response, itemMapper) {
    const root = extractRoot(response);
    const items = Array.isArray(root.data)
        ? root.data.map((item) => itemMapper({
            ...item,
            snapshotPayload: parseJsonPayload(item.snapshotPayload || item.snapshot_payload),
            changedAt: item.changedAt || item.createdAt || item.created_at,
        }))
        : [];
    return {
        ...response,
        data: {
            ...root,
            data: items,
        },
    };
}

export function getUnifiedReviewRecords(apiBaseurl = "", params = {}, httpClient, itemMapper = (item) => item) {
    const http = resolveHttpClient(httpClient);
    const resolvedBaseUrl = resolveBaseUrl(apiBaseurl, http);
    return withManagerFallback(
        () => http.post(buildUnifiedReviewPath(resolvedBaseUrl, "/query"), params || {}),
        () => http.post(buildManagerUnifiedReviewPath(resolvedBaseUrl, "/query"), params || {})
    ).then((response) => normalizeListResponse(response, itemMapper));
}

export function getUnifiedReviewHistory(apiBaseurl = "", riskEventId, httpClient, itemMapper = (item) => item) {
    if (!riskEventId) {
        throw new Error("riskEventId 不能为空");
    }
    const http = resolveHttpClient(httpClient);
    const resolvedBaseUrl = resolveBaseUrl(apiBaseurl, http);
    return withManagerFallback(
        () => http.get(buildUnifiedReviewPath(resolvedBaseUrl, `/${riskEventId}/history`), { params: {} }),
        () => http.get(buildManagerUnifiedReviewPath(resolvedBaseUrl, `/${riskEventId}/history`), { params: {} })
    ).then((response) => normalizeHistoryResponse(response, itemMapper));
}

export function saveUnifiedReviewDecision(apiBaseurl = "", riskEventId, payload = {}, httpClient, itemMapper = (item) => item) {
    if (!riskEventId) {
        throw new Error("riskEventId 不能为空");
    }
    if (!payload.decision) {
        throw new Error("payload.decision 必须提供");
    }
    const http = resolveHttpClient(httpClient);
    const resolvedBaseUrl = resolveBaseUrl(apiBaseurl, http);
    return withManagerFallback(
        () => http.post(buildUnifiedReviewPath(resolvedBaseUrl, `/${riskEventId}/decision`), payload),
        () => http.post(buildManagerUnifiedReviewPath(resolvedBaseUrl, `/${riskEventId}/decision`), payload)
    ).then((response) => {
        const root = extractRoot(response);
        return {
            ...response,
            data: {
                ...root,
                data: itemMapper(normalizeRecord(root.data || {})),
            },
        };
    });
}

export function saveUnifiedReviewBatchDecision(apiBaseurl = "", payload = {}, httpClient) {
    if (!Array.isArray(payload.riskEventIds) || !payload.riskEventIds.length) {
        throw new Error("payload.riskEventIds 必须提供");
    }
    if (!payload.decision) {
        throw new Error("payload.decision 必须提供");
    }
    const http = resolveHttpClient(httpClient);
    const resolvedBaseUrl = resolveBaseUrl(apiBaseurl, http);
    return withManagerFallback(
        () => http.post(buildUnifiedReviewPath(resolvedBaseUrl, "/batch-decision"), payload),
        () => http.post(buildManagerUnifiedReviewPath(resolvedBaseUrl, "/batch-decision"), payload)
    );
}
